import { useEffect, useState } from "react";

import {
  ArrayParam,
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
} from "use-query-params";

import { IUser, TableParams } from "../../../../types";
import { isEmail } from "../../../../utils";
import { ContentWrapper } from "../../../common/styledComponents";
import useEmailParam from "../../../hooks/useEmailParam";
import UserFilters, { UserFilterOptions } from "../Common/UserFilters";
import { CreateUser } from "./CreateUser";
import UserList from "./UserList";
import { HeaderRow } from "./styles";
import { useAuth } from "../../../../../modules/auth/useAuth";
import { isFirm } from "@/modules/firms";
import {
  clientStateFromTableParams,
  seedTableParams,
  sortFieldFromTableParams,
} from "./viewStateParams";

const ActivationView = () => {
  const [emailParam, setEmailParam] = useEmailParam();

  // Persist the view state in the URL so a "Load all" list plus a Client-state
  // filter (and sort/page) survive a browser reload instead of resetting to an
  // empty view (SMT-1247, AC2). `email` is persisted separately via
  // useEmailParam; these five seed the initial state and are then mirrored back
  // as one batched update.
  const [viewQuery, setViewQuery] = useQueryParams({
    all: BooleanParam,
    clientState: ArrayParam,
    sortField: StringParam,
    sortOrder: StringParam,
    page: NumberParam,
    firm: StringParam,
  });

  const { getOperatorId, getOperatorFirm } = useAuth();

  const [searchParam, setSearchParam] = useState<string | null | undefined>(
    emailParam
  );
  const [filterOptions, setFilterOptions] = useState<UserFilterOptions>({
    search: searchParam || "",
    all: viewQuery.all ?? !searchParam,
    firm:
      viewQuery.firm === "all" || isFirm(viewQuery.firm)
        ? viewQuery.firm
        : getOperatorFirm(),
  });

  const [tableParams, setTableParams] = useState<TableParams>(() =>
    seedTableParams({
      clientState: viewQuery.clientState,
      sortField: viewQuery.sortField,
      sortOrder: viewQuery.sortOrder,
      page: viewQuery.page,
    })
  );

  // Mirror the active view state back into the URL as a single batched update,
  // so concurrent changes (e.g. Load all clearing filter + sort + page) can't
  // clobber each other — use-query-params does not batch separate setters
  // (SMT-1247).
  useEffect(() => {
    const current = tableParams.pagination?.current;
    setViewQuery({
      all: filterOptions.all || undefined,
      clientState: clientStateFromTableParams(tableParams),
      sortField: sortFieldFromTableParams(tableParams),
      sortOrder: (tableParams.order as string) || undefined,
      page: current && current !== 1 ? current : undefined,
      firm: filterOptions.firm,
    });
  }, [filterOptions.all, filterOptions.firm, tableParams, setViewQuery]);

  const handleCreateUser = (newUser: IUser) => {
    // First set email param to ensure it's stored for highlighting
    setEmailParam(newUser.email);
    setSearchParam(newUser.email);

    // Update filter options to search for the new user
    setFilterOptions({
      ...filterOptions,
      search: newUser.email,
      all: false,
    });

    // Reset pagination to first page to ensure the user is visible
    setTableParams((prev: TableParams) => ({
      ...prev,
      pagination: { ...prev.pagination, current: 1 },
    }));
  };

  return (
    <ContentWrapper>
      <HeaderRow>
        <UserFilters
          filterOptions={filterOptions}
          withMyCases
          setFilterOptions={async (updatedFilterOptions: UserFilterOptions) => {
            // "Load all" is a fresh browse: clear the search, the email
            // selection, and the Client-state filter + sort so the whole list
            // shows, not a stale filtered/selected view (SMT-1247).
            const isLoadAll =
              updatedFilterOptions.all === true &&
              !updatedFilterOptions.myCases;

            if (!updatedFilterOptions.all) {
              setSearchParam(updatedFilterOptions.search);
              isEmail(updatedFilterOptions.search || "")
                ? setEmailParam(updatedFilterOptions.search)
                : setEmailParam(undefined);
            }

            if (isLoadAll) {
              setSearchParam(undefined);
              setEmailParam(undefined);
            }

            let resolvedAssigneeId = updatedFilterOptions.assigneeId;
            if (updatedFilterOptions.myCases) {
              // The acting operator's accounts id comes straight from the access
              // token (always present as a required claim).
              resolvedAssigneeId = getOperatorId();
            } else if (updatedFilterOptions.myCases === false) {
              resolvedAssigneeId = updatedFilterOptions.assigneeId;
            }

            // Only treat this as "my cases" once the operator actually
            // resolved; otherwise a failed self-lookup would fall through to
            // all=true (every case) instead of the intended filter.
            const isResolvedMyCases =
              Boolean(updatedFilterOptions.myCases) &&
              Boolean(resolvedAssigneeId);

            setFilterOptions({
              ...filterOptions,
              ...updatedFilterOptions,
              myCases: isResolvedMyCases,
              assigneeId: resolvedAssigneeId,
              all: isResolvedMyCases ? true : updatedFilterOptions.all,
            });
            setTableParams(
              isLoadAll
                ? { pagination: { current: 1, pageSize: 10 } }
                : {
                    ...tableParams,
                    pagination: {
                      ...tableParams.pagination,
                      current: 1,
                    },
                  }
            );
          }}
        />
        <CreateUser onCreateUser={handleCreateUser} />
      </HeaderRow>
      <UserList
        filterOptions={filterOptions}
        setEmailParam={setEmailParam}
        tableParams={tableParams}
        setTableParams={setTableParams}
        setFilterOptions={setFilterOptions}
      />
    </ContentWrapper>
  );
};

export default ActivationView;
